// OccasionCategories.jsx — Occasion grid with MUI icons + correct filtered links

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";

// MUI Icons
import CakeOutlinedIcon           from "@mui/icons-material/CakeOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import ChildCareOutlinedIcon      from "@mui/icons-material/ChildCareOutlined";
import DiamondOutlinedIcon        from "@mui/icons-material/DiamondOutlined";
import CelebrationOutlinedIcon    from "@mui/icons-material/CelebrationOutlined";
import CasinoOutlinedIcon         from "@mui/icons-material/CasinoOutlined";
import LocalFloristOutlinedIcon   from "@mui/icons-material/LocalFloristOutlined";
import CardGiftcardOutlinedIcon   from "@mui/icons-material/CardGiftcardOutlined";
import ArrowForwardIcon           from "@mui/icons-material/ArrowForward";
import AutoAwesomeIcon            from "@mui/icons-material/AutoAwesome";

const OCCASIONS = [
  {
    label: "Birthday",
    Icon: CakeOutlinedIcon,
    gradient: "from-pink-400 to-rose-500",
    iconBg: "#ffe4e8",
    iconColor: "#f0155a",
    link: "/?occasion=birthday",
    count: "8+ Gifts",
  },
  {
    label: "Anniversary",
    Icon: FavoriteBorderOutlinedIcon,
    gradient: "from-red-400 to-pink-500",
    iconBg: "#fee2e2",
    iconColor: "#dc2626",
    link: "/?occasion=anniversary",
    count: "6+ Gifts",
  },
  {
    label: "Kids",
    Icon: ChildCareOutlinedIcon,
    gradient: "from-yellow-400 to-amber-500",
    iconBg: "#fef3c7",
    iconColor: "#d97706",
    link: "/?occasion=kids",
    count: "5+ Toys",
  },
  {
    label: "Wedding",
    Icon: DiamondOutlinedIcon,
    gradient: "from-purple-400 to-violet-600",
    iconBg: "#ede9fe",
    iconColor: "#7c3aed",
    link: "/?occasion=wedding",
    count: "4+ Gifts",
  },
  {
    label: "Festival",
    Icon: CelebrationOutlinedIcon,
    gradient: "from-orange-400 to-red-500",
    iconBg: "#ffedd5",
    iconColor: "#ea580c",
    link: "/?occasion=festival",
    count: "6+ Items",
  },
  {
    label: "Games Night",
    Icon: CasinoOutlinedIcon,
    gradient: "from-blue-400 to-indigo-600",
    iconBg: "#dbeafe",
    iconColor: "#2563eb",
    link: "/?occasion=games",
    count: "5+ Games",
  },
  {
    label: "Thank You",
    Icon: LocalFloristOutlinedIcon,
    gradient: "from-emerald-400 to-teal-500",
    iconBg: "#d1fae5",
    iconColor: "#059669",
    link: "/?occasion=thankyou",
    count: "5+ Gifts",
  },
  {
    label: "All Gifts",
    Icon: CardGiftcardOutlinedIcon,
    gradient: "from-rose-500 to-fuchsia-600",
    iconBg: "#ffe0ea",
    iconColor: "#f0155a",
    link: "/products",
    count: "23+ Products",
  },
];

const containerVariants = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.07 } },
};
const itemVariants = {
  hidden:  { opacity: 0, y: 28, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] } },
};

export default function OccasionCategories() {
  const [offerSettings, setOfferSettings] = useState({
    offerEnabled: true,
    offerLabel: "Limited Offer",
    offerTitle1: "Free Delivery",
    offerTitle2: "Today Only",
    offerDescription: "Use code FREESHIP at checkout. No minimum order required.",
    offerButtonText: "Claim Offer",
    offerLink: "/products",
  });

  useEffect(() => {
    async function fetchOfferSettings() {
      try {
        const snap = await getDoc(doc(db, "settings", "general"));
        if (snap.exists()) {
          const data = snap.data();
          if (data.offerEnabled !== undefined) {
            setOfferSettings(prev => ({ ...prev, ...data }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch offer settings:", err);
      }
    }
    fetchOfferSettings();
  }, []);

  return (
    <section className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-rose-500 mb-3 bg-rose-50 px-4 py-1.5 rounded-full">
            <AutoAwesomeIcon sx={{ fontSize: 13 }} /> Shop by Occasion
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Every Moment Deserves{" "}
            <span className="text-gradient-gift">Something Special</span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-xl mx-auto">
            Whether it's a birthday, anniversary, or a simple treat for yourself — we've got you covered.
          </p>
        </motion.div>

        {/* Occasion grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-2 min-[480px]:grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-4 sm:gap-6"
        >
          {OCCASIONS.map((occasion) => (
            <motion.div key={occasion.label} variants={itemVariants}>
              <Link to={occasion.link} className="group flex flex-col items-center gap-3 occasion-circle">
                {/* Circle icon */}
                <div className="relative">
                  <div
                    className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow"
                    style={{ background: `linear-gradient(135deg, ${occasion.iconBg}, white)`, border: `2px solid ${occasion.iconColor}20` }}
                  >
                    <occasion.Icon sx={{ fontSize: 32, color: occasion.iconColor }} />
                  </div>
                  {/* Glow ring on hover */}
                  <div
                    className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-30 scale-110 group-hover:scale-125 transition-all duration-300"
                    style={{ background: `linear-gradient(135deg, ${occasion.iconColor}40, transparent)` }}
                  />
                </div>

                {/* Label */}
                <div className="text-center">
                  <p className="text-xs sm:text-sm font-semibold text-gray-800 group-hover:text-rose-600 transition-colors leading-tight">
                    {occasion.label}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5 hidden sm:block">
                    {occasion.count}
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Banner CTA */}
        {offerSettings.offerEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.3 }}
            className="mt-14 rounded-3xl overflow-hidden relative"
            style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" }}
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-rose-500/20 blur-2xl" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-purple-500/20 blur-2xl" />

            <div className="relative px-6 py-8 sm:px-10 md:px-14 flex flex-col md:flex-row items-center md:items-start justify-between gap-6 text-center md:text-left">
              <div>
                <p className="text-white/60 text-sm font-medium uppercase tracking-widest mb-2">{offerSettings.offerLabel}</p>
                <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                  {offerSettings.offerTitle1} <span className="text-gradient-gold">{offerSettings.offerTitle2}</span>
                </h3>
                <p className="text-white/70 text-base">
                  {offerSettings.offerDescription}
                </p>
              </div>
              <Link
                to={offerSettings.offerLink || "/products"}
                className="flex-shrink-0 inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-600 text-white font-bold px-6 py-3 sm:px-8 sm:py-4 rounded-2xl hover:opacity-90 transition-all shadow-gift hover:shadow-gift-lg hover:-translate-y-0.5 text-xs sm:text-sm whitespace-nowrap"
              >
                {offerSettings.offerButtonText} <ArrowForwardIcon sx={{ fontSize: 18 }} />
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
